import React, { useEffect, useState } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { FaLinkedin, FaExternalLinkAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { fetchLatestPosts } from '../services/linkedinService';
import { getImageUrl } from '../constants';
import { renderTextWithLinks } from '../utils/textRender';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const truncate = (text, max) => {
  if (!text) return '';
  const clean = text.trim();
  return clean.length > max ? `${clean.substring(0, max)}…` : clean;
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  try {
    return new Date(Number(timestamp)).toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
};

const getPostImage = (post) => {
  const raw = post?.mediaThumbnail || post?.article?.thumbnail || null;
  return raw ? getImageUrl(raw) : null;
};

const isNewPost = (post) => post?.publishedAt && Number(post.publishedAt) >= Date.now() - SEVEN_DAYS_MS;

export default function LinkedInPreview() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetchLatestPosts(2)
      .then((items) => {
        if (mounted) setPosts(items);
      })
      .catch((err) => console.error('Failed to load latest LinkedIn posts:', err))
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const featured = posts[0];
  const secondary = posts[1];
  const featuredImage = featured ? getPostImage(featured) : null;

  return (
    <Card className="shadow-sm border-0 mb-4 overflow-hidden" dir="rtl" style={{ borderRadius: 15 }}>
      <Card.Header
        className="d-flex align-items-center justify-content-between py-3"
        style={{
          background: 'linear-gradient(135deg,#0a66c2,#004182)',
          color: '#fff',
          border: 'none',
          flexWrap: 'wrap',
          rowGap: 8,
          columnGap: 8
        }}
      >
        <span className="d-flex align-items-center gap-2 fw-bold" style={{ flex: '1 1 auto', minWidth: 0 }}>
          <FaLinkedin size={20} style={{ flexShrink: 0 }} />
          מהלינקדאין שלנו
        </span>
        <button
          type="button"
          onClick={() => navigate('/company-posts')}
          style={{
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: '#fff',
            fontSize: '0.78rem',
            cursor: 'pointer',
            borderRadius: 20,
            padding: '4px 12px',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          לכל הפוסטים ←
        </button>
      </Card.Header>

      <Card.Body className="p-0">
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" variant="primary" />
          </div>
        )}

        {!loading && posts.length === 0 && (
          <p className="text-muted text-center small mb-0 py-3 px-3">אין פוסטים להצגה כרגע.</p>
        )}

        {/* Featured post */}
        {!loading && featured && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/company-posts')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/company-posts')}
            style={{ cursor: 'pointer' }}
          >
            {featuredImage ? (
              <img
                src={featuredImage}
                alt=""
                style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }}
                onError={(e) => { if (e.target) e.target.style.display = 'none'; }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: 90,
                  background: 'linear-gradient(135deg,#0a66c2,#004182)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.85)'
                }}
              >
                <FaLinkedin size={40} />
              </div>
            )}
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">{formatDate(featured.publishedAt)}</small>
                {isNewPost(featured) && (
                  <span style={{ background: '#bf2e1a', color: '#fff', fontSize: '0.62rem', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
                    NEW
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.88rem', lineHeight: 1.5, color: '#1a202c' }}>
                {renderTextWithLinks(truncate(featured.commentary, 160)) || 'לחצו לצפייה בפוסט'}
              </div>
              {featured.permalink && (
                <a
                  href={featured.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ fontSize: '0.78rem', color: '#0a66c2', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, textDecoration: 'none' }}
                >
                  פתח בלינקדאין <FaExternalLinkAlt size={11} />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Secondary post (compact) */}
        {!loading && secondary && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/company-posts')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/company-posts')}
            className="d-flex gap-2 p-3"
            style={{ cursor: 'pointer', borderTop: '1px solid #eee' }}
          >
            {getPostImage(secondary) && (
              <img
                src={getPostImage(secondary)}
                alt=""
                style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                onError={(e) => { if (e.target) e.target.style.display = 'none'; }}
              />
            )}
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <small className="text-muted">{formatDate(secondary.publishedAt)}</small>
              <div style={{ fontSize: '0.8rem', lineHeight: 1.4, color: '#334155' }}>
                {truncate(secondary.commentary, 70) || 'לחצו לצפייה בפוסט'}
              </div>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
